import React, { useEffect, useState } from "react";
import { Modal, Button, Confirm } from "semantic-ui-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Application } from "../types";
import StudentApplicationList from "./StudentApplicationList";
import { ShadowingWithApplications } from "../../shadowings/types";
import { ApplicationStatus } from "../../../app/enums/applicationStatus";
import agent from "../../../app/api/agent";
import useToaster from "../../../app/hooks/useToaster";
import { fetchMyShadowings } from "../../shadowings/actions";
import { useAppDispatch } from "../../../app/hooks/useAppDispatch";

interface IProps {
  shadowing: ShadowingWithApplications;
  approved: boolean;
}

const ApplicationsModal: React.FC<IProps> = ({ shadowing, approved }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const { success } = useToaster();
  const dispatch = useAppDispatch();

  const [pendingApplications, setPendingApplications] = useState<Application[]>([]);
  const [approvedApplications, setApprovedApplications] = useState<Application[]>([]);
  const [cancelledApplications, setCancelledApplications] = useState<Application[]>([]);
  const [pendingApprovalId, setPendingApprovalId] = useState<number | null>(null);

  const doApprove = (applicationId: number, approved: boolean) => {
    const approvedApplication = pendingApplications.find((x) => x.id === applicationId);
    if (!approvedApplication) return;

    agent.Applications.approve(applicationId, approved).then(() => {
      if (approved) {
        setApprovedApplications([...approvedApplications, approvedApplication]);
        setPendingApplications(pendingApplications.filter((application) => application.id !== applicationId));
        success(t("applications.notifications.application_confirmed"));
        dispatch(fetchMyShadowings({ past: false }, false));
      } else {
        setPendingApplications(pendingApplications.filter((application) => application.id !== applicationId));
        success(t("applications.notifications.application_rejected"));
      }
    });
  };

  const handleApplicationConfirmation = (applicationId: number, approved: boolean) => {
    const approvedApplication = pendingApplications.find((x) => x.id === applicationId);
    if (!approvedApplication) return;

    if (approved) {
      const isLastSpot = approvedApplications.length === shadowing.participantsCapacity - 1;
      if (isLastSpot) {
        setPendingApprovalId(applicationId);
        return;
      }
    }

    doApprove(applicationId, approved);
  };

  const handleApplicationCancellation = (applicationId: number) => {
    agent.Applications.cancel(applicationId).then(() => {
      setApprovedApplications(approvedApplications.filter((application) => application.id !== applicationId));
      success(t("applications.notifications.application_cancelled"));
    });
  };

  const applications = approved
    ? approvedApplications.concat(cancelledApplications)
    : pendingApplications.concat(cancelledApplications);

  useEffect(() => {
    if (!shadowing.applications) return;

    setPendingApplications(
      shadowing.applications.filter((application) =>
        [ApplicationStatus.WAITING_FOR_COMPANY_APPROVAL, ApplicationStatus.WAITING_FOR_SCHOOL_APPROVAL].includes(
          application.status
        )
      )
    );

    setApprovedApplications(
      shadowing.applications.filter((application) => application.status === ApplicationStatus.COMPANY_APPROVED)
    );

    setCancelledApplications(
      shadowing.applications.filter((application) =>
        [ApplicationStatus.STUDENT_CANCELLED, ApplicationStatus.COMPANY_CANCELLED].includes(application.status)
      )
    );
  }, [shadowing]);

  const renderModalTrigger = () => {
    if (approved) {
      if (approvedApplications.length === 0) return null;

      return (
        <Link to="#">
          {t("common.student_applications")}: {approvedApplications.length}
        </Link>
      );
    } else {
      if (pendingApplications.length === 0) return null;

      return (
        <Button primary>
          {t("applications.company.see_new_applications")} ({pendingApplications.length})
        </Button>
      );
    }
  };

  return (
    <>
      <Modal
        dimmer="inverted"
        closeOnEscape={true}
        closeOnDimmerClick={true}
        closeIcon
        onClose={() => setOpen(false)}
        onOpen={() => setOpen(true)}
        open={open}
        trigger={renderModalTrigger()}
      >
        <Modal.Header>
          {'"'}
          {shadowing.position}
          {'" '}
          {approved ? t("applications.company.approved_applications") : t("applications.company.new_applications")}
        </Modal.Header>
        <Modal.Content>
          <Modal.Description>
            {applications.length > 0 ? (
              <StudentApplicationList
                applications={applications}
                handleConfirmation={handleApplicationConfirmation}
                handleCancellation={handleApplicationCancellation}
              />
            ) : (
              <span>{t("common.no_students_to_position")}.</span>
            )}
          </Modal.Description>
        </Modal.Content>
        <Modal.Actions></Modal.Actions>
      </Modal>
      <Confirm
        size="tiny"
        open={pendingApprovalId !== null}
        content={t("applications.notifications.approve_last_spot_confirm")}
        confirmButton={t("common.actions.confirm")}
        cancelButton={t("common.actions.cancel")}
        onCancel={() => setPendingApprovalId(null)}
        onConfirm={() => {
          if (pendingApprovalId !== null) doApprove(pendingApprovalId, true);
          setPendingApprovalId(null);
        }}
      />
    </>
  );
};

export default ApplicationsModal;
